import { useState, useEffect, useCallback } from "react";

// ── Theme tokens ──
const T = {
  bg: "#0a0e17",
  surface: "#111827",
  surfaceAlt: "#0d1321",
  border: "#1e293b",
  borderHi: "#334155",
  text: "#e2e8f0",
  textDim: "#64748b",
  textMuted: "#475569",
  green: "#22c55e",
  greenDim: "#166534",
  greenBg: "rgba(34,197,94,0.06)",
  red: "#ef4444",
  redDim: "#991b1b",
  redBg: "rgba(239,68,68,0.06)",
  amber: "#f59e0b",
  amberDim: "#92400e",
  amberBg: "rgba(245,158,11,0.06)",
  blue: "#3b82f6",
  blueDim: "#1e3a5f",
  cyan: "#06b6d4",
  purple: "#a78bfa",
};

const ASSET_COLOR = { NQ: T.cyan, QQQ: T.purple, GC: "#fbbf24", CL: "#f87171", ZN: "#34d399" };
const HEALTH_ASSETS = ["NQ", "QQQ", "GC", "CL", "ZN"];

// ── Helpers ──
function formatTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZone: "UTC",
  });
}

function classifySignal(name) {
  const n = name.toUpperCase();
  if (n.includes("COBRA")) return { group: "Cobra", color: T.cyan };
  if (n.includes("LUX")) return { group: "Lux", color: T.purple };
  if (n.includes("VGB")) return { group: "VGB", color: T.amber };
  if (n.includes("SMART_BAND")) return { group: "SmartBand", color: T.blue };
  if (n.includes("SMART_TRAIL")) return { group: "SmartTrail", color: "#10b981" };
  if (n.includes("ALGO_PRO")) return { group: "AlgoPro", color: "#f472b6" };
  if (n.includes("EMA")) return { group: "EMA", color: "#fb923c" };
  return { group: "Other", color: T.textDim };
}

// ── Stat Card ──
function StatCard({ label, value, sub, accent }) {
  return (
    <div
      style={{
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: 8,
        padding: "16px 20px",
        flex: "1 1 0",
        minWidth: 140,
      }}
    >
      <div style={{ fontSize: 11, color: T.textDim, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: accent || T.text, fontFamily: "'JetBrains Mono', monospace" }}>
        {value ?? "—"}
      </div>
      {sub && <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

// ── Signal Pill ──
function SignalPill({ data, session }) {
  if (!data) return <span style={{ color: T.textMuted, fontSize: 11 }}>—</span>;
  const { active, count, last_fired } = data;
  const bg = active ? T.greenBg : T.redBg;
  const borderColor = active ? T.greenDim : T.redDim;
  const dotColor = active ? T.green : T.red;

  return (
    <div
      style={{
        background: bg,
        border: `1px solid ${borderColor}`,
        borderRadius: 6,
        padding: "4px 10px",
        display: "flex",
        alignItems: "center",
        gap: 8,
        minWidth: 130,
      }}
    >
      <div
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: dotColor,
          boxShadow: active ? `0 0 6px ${dotColor}` : "none",
          flexShrink: 0,
        }}
      />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 11, color: T.textDim }}>{session}</div>
        <div style={{ fontSize: 12, fontFamily: "'JetBrains Mono', monospace", color: active ? T.text : T.textMuted }}>
          {active ? formatTime(last_fired) : "silent"}
        </div>
      </div>
      {active && (
        <div
          style={{
            fontSize: 10,
            background: "rgba(34,197,94,0.15)",
            color: T.green,
            borderRadius: 4,
            padding: "2px 6px",
            fontWeight: 600,
            fontFamily: "'JetBrains Mono', monospace",
          }}
        >
          ×{count}
        </div>
      )}
    </div>
  );
}

// ── Signal Row ──
function SignalRow({ name, ethData, rthData }) {
  const { group, color } = classifySignal(name);
  const bothSilent = !ethData?.active && !rthData?.active;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "200px 1fr 1fr",
        gap: 8,
        padding: "6px 16px",
        borderBottom: `1px solid ${T.border}`,
        opacity: bothSilent ? 0.5 : 1,
        background: bothSilent ? "rgba(239,68,68,0.02)" : "transparent",
        transition: "background 0.15s",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.02)")}
      onMouseLeave={(e) => (e.currentTarget.style.background = bothSilent ? "rgba(239,68,68,0.02)" : "transparent")}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ width: 3, height: 20, borderRadius: 2, background: color, flexShrink: 0 }} />
        <div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              fontFamily: "'JetBrains Mono', monospace",
              color: bothSilent ? T.textMuted : T.text,
              letterSpacing: "-0.01em",
            }}
          >
            {name}
          </div>
          <div style={{ fontSize: 9, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            {group}
          </div>
        </div>
      </div>
      <SignalPill data={ethData} session="ETH" />
      <SignalPill data={rthData} session="RTH" />
    </div>
  );
}

// ── Timeframe Section ──
function TimeframeSection({ timeframe, data, isExpanded, onToggle, filter }) {
  if (!data || data.error) return null;
  const signals = data.signals || {};
  const signalNames = Object.keys(signals).sort((a, b) => {
    const aActive = signals[a].ETH?.active || signals[a].RTH?.active;
    const bActive = signals[b].ETH?.active || signals[b].RTH?.active;
    if (aActive && !bActive) return -1;
    if (!aActive && bActive) return 1;
    return a.localeCompare(b);
  });

  const filtered = signalNames.filter((name) => {
    if (filter === "active") return signals[name].ETH?.active || signals[name].RTH?.active;
    if (filter === "silent") return !signals[name].ETH?.active && !signals[name].RTH?.active;
    return true;
  });

  const activeCount = signalNames.filter((n) => signals[n].ETH?.active || signals[n].RTH?.active).length;
  const silentCount = signalNames.length - activeCount;

  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden" }}>
      {/* Header */}
      <div
        onClick={onToggle}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 20px",
          cursor: "pointer",
          background: T.surfaceAlt,
          borderBottom: isExpanded ? `1px solid ${T.border}` : "none",
          userSelect: "none",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              fontFamily: "'JetBrains Mono', monospace",
              color: T.cyan,
              background: T.blueDim,
              padding: "4px 10px",
              borderRadius: 6,
              letterSpacing: "0.02em",
            }}
          >
            {timeframe}
          </div>
          <div style={{ fontSize: 12, color: T.textDim }}>{data.table}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: T.green }} />
            <span style={{ fontSize: 12, color: T.green, fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" }}>
              {activeCount}
            </span>
          </div>
          {silentCount > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: T.red }} />
              <span style={{ fontSize: 12, color: T.red, fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" }}>
                {silentCount}
              </span>
            </div>
          )}
          <div
            style={{
              fontSize: 16,
              color: T.textDim,
              transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s",
            }}
          >
            ▾
          </div>
        </div>
      </div>

      {/* Column headers + rows */}
      {isExpanded && (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "200px 1fr 1fr",
              gap: 8,
              padding: "8px 16px",
              borderBottom: `1px solid ${T.border}`,
            }}
          >
            <div style={{ fontSize: 10, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.1em" }}>Signal</div>
            <div style={{ fontSize: 10, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.1em" }}>ETH (Extended)</div>
            <div style={{ fontSize: 10, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.1em" }}>RTH (Regular)</div>
          </div>
          {filtered.map((name) => (
            <SignalRow key={name} name={name} ethData={signals[name].ETH} rthData={signals[name].RTH} />
          ))}
          {filtered.length === 0 && (
            <div style={{ padding: 20, textAlign: "center", color: T.textMuted, fontSize: 12 }}>
              No signals match current filter
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Asset Panel ──
function AssetPanel({ asset, data, expanded, onToggle, tfExpanded, onTfToggle, filter, search }) {
  const color = ASSET_COLOR[asset] || T.text;
  const summary = data?.summary;

  const getFilteredData = (tfData) => {
    if (!tfData?.signals || !search) return tfData;
    const s = search.toUpperCase();
    const filtered = {};
    Object.entries(tfData.signals).forEach(([name, val]) => {
      if (name.includes(s)) filtered[name] = val;
    });
    return { ...tfData, signals: filtered };
  };

  const tfOrder = ["1m"];

  return (
    <div
      style={{
        border: `1px solid ${T.border}`,
        borderRadius: 12,
        overflow: "hidden",
        background: T.surfaceAlt,
      }}
    >
      {/* Asset header */}
      <div
        onClick={onToggle}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 24px",
          cursor: "pointer",
          borderBottom: expanded ? `1px solid ${T.border}` : "none",
          userSelect: "none",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div
            style={{
              fontSize: 16,
              fontWeight: 700,
              fontFamily: "'JetBrains Mono', monospace",
              color,
              background: `${color}18`,
              border: `1px solid ${color}40`,
              padding: "5px 14px",
              borderRadius: 7,
              letterSpacing: "0.06em",
            }}
          >
            {asset}
          </div>
          {summary ? (
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <span style={{ fontSize: 12, color: T.textDim }}>{summary.total_unique_signals} signals</span>
              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <div style={{ width: 7, height: 7, borderRadius: "50%", background: T.green }} />
                <span style={{ fontSize: 12, color: T.green, fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" }}>
                  {summary.fired_today} fired
                </span>
              </div>
              {summary.silent_today > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <div style={{ width: 7, height: 7, borderRadius: "50%", background: T.red }} />
                  <span style={{ fontSize: 12, color: T.red, fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" }}>
                    {summary.silent_today} silent
                  </span>
                </div>
              )}
            </div>
          ) : data?.error ? (
            <span style={{ fontSize: 12, color: T.red }}>Load failed</span>
          ) : (
            <span style={{ fontSize: 12, color: T.textMuted }}>Loading…</span>
          )}
        </div>
        <div
          style={{
            fontSize: 16,
            color: T.textDim,
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
          }}
        >
          ▾
        </div>
      </div>

      {/* Silent signals alert */}
      {expanded && summary?.silent_signals?.length > 0 && (
        <div
          style={{
            background: T.redBg,
            borderBottom: `1px solid ${T.redDim}`,
            padding: "12px 24px",
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 600, color: T.red, marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
            <span>⚠</span> Silent — No data today
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
            {summary.silent_signals.map((sig) => (
              <span
                key={sig}
                style={{
                  fontSize: 10,
                  fontFamily: "'JetBrains Mono', monospace",
                  background: "rgba(239,68,68,0.1)",
                  border: `1px solid ${T.redDim}`,
                  color: T.red,
                  padding: "2px 7px",
                  borderRadius: 4,
                }}
              >
                {sig}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Timeframe sections */}
      {expanded && (
        <div style={{ padding: "14px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
          {data?.error ? (
            <div style={{ color: T.red, fontSize: 12, padding: "12px 0" }}>
              Failed to load {asset} data: {data.error}
            </div>
          ) : (
            tfOrder.map((tf) => {
              const tfData = data?.timeframes?.[tf];
              if (!tfData) return null;
              const displayData = search ? getFilteredData(tfData) : tfData;
              const key = `${asset}_${tf}`;
              return (
                <TimeframeSection
                  key={key}
                  timeframe={tf}
                  data={displayData}
                  isExpanded={tfExpanded[key] ?? true}
                  onToggle={() => onTfToggle(key)}
                  filter={filter}
                />
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Page ──
export default function SignalHealthCheck({ apiBaseUrl = window.location.origin }) {
  const [assetData, setAssetData] = useState(Object.fromEntries(HEALTH_ASSETS.map((a) => [a, null])));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [assetExpanded, setAssetExpanded] = useState(Object.fromEntries(HEALTH_ASSETS.map((a) => [a, true])));
  const [tfExpanded, setTfExpanded] = useState({});
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const responses = await Promise.all(
        HEALTH_ASSETS.map((a) => fetch(`${apiBaseUrl}/strategies/signals/health/?asset=${a}`))
      );
      const jsons = await Promise.all(responses.map((r) => r.json()));
      setAssetData(
        Object.fromEntries(
          HEALTH_ASSETS.map((a, i) => [
            a,
            jsons[i].success ? jsons[i] : { error: jsons[i].error || "Unknown error" },
          ])
        )
      );
      setLastRefresh(new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Expand all timeframe sections by default once data loads
  useEffect(() => {
    const exp = {};
    HEALTH_ASSETS.forEach((asset) => {
      const d = assetData[asset];
      if (d?.timeframes) {
        Object.keys(d.timeframes).forEach((tf) => {
          exp[`${asset}_${tf}`] = true;
        });
      }
    });
    if (Object.keys(exp).length > 0) setTfExpanded(exp);
  }, [assetData]);

  const toggleAsset = (asset) => setAssetExpanded((prev) => ({ ...prev, [asset]: !prev[asset] }));
  const toggleTf = (key) => setTfExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  // Combined summary across all assets
  const cs = HEALTH_ASSETS.reduce(
    (acc, asset) => {
      const s = assetData[asset]?.summary;
      if (s) {
        acc.total += s.total_unique_signals || 0;
        acc.fired += s.fired_today || 0;
        acc.silent += s.silent_today || 0;
      }
      return acc;
    },
    { total: 0, fired: 0, silent: 0 }
  );

  const date = HEALTH_ASSETS.map((a) => assetData[a]?.date).find(Boolean);

  // ── Loading state ──
  if (loading && HEALTH_ASSETS.every((a) => !assetData[a])) {
    return (
      <div style={{ background: T.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ color: T.textDim, fontFamily: "'JetBrains Mono', monospace", fontSize: 14 }}>
          Scanning signals…
        </div>
      </div>
    );
  }

  if (error && HEALTH_ASSETS.every((a) => !assetData[a])) {
    return (
      <div style={{ background: T.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ color: T.red, fontFamily: "'JetBrains Mono', monospace", fontSize: 14 }}>Error: {error}</div>
      </div>
    );
  }

  return (
    <div
      style={{
        background: T.bg,
        minHeight: "100vh",
        color: T.text,
        fontFamily: "'Inter', -apple-system, sans-serif",
      }}
    >
      <link href="https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />

      {/* ── Header ── */}
      <div
        style={{
          borderBottom: `1px solid ${T.border}`,
          padding: "20px 32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: T.surfaceAlt,
        }}
      >
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
            <div style={{ fontSize: 20, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", letterSpacing: "-0.02em" }}>
              Signal Health
            </div>
            {/* Asset badges */}
            <div style={{ display: "flex", gap: 6 }}>
              {HEALTH_ASSETS.map((a) => (
                <div
                  key={a}
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    fontFamily: "'JetBrains Mono', monospace",
                    color: ASSET_COLOR[a],
                    background: `${ASSET_COLOR[a]}18`,
                    border: `1px solid ${ASSET_COLOR[a]}40`,
                    padding: "2px 8px",
                    borderRadius: 4,
                  }}
                >
                  {a}
                </div>
              ))}
            </div>
            <div
              style={{
                fontSize: 10,
                fontWeight: 600,
                background: cs.silent > 0 ? T.amberBg : T.greenBg,
                color: cs.silent > 0 ? T.amber : T.green,
                border: `1px solid ${cs.silent > 0 ? T.amberDim : T.greenDim}`,
                padding: "3px 8px",
                borderRadius: 4,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
              }}
            >
              {cs.silent > 0 ? `${cs.silent} silent` : "All clear"}
            </div>
          </div>
          <div style={{ fontSize: 12, color: T.textDim }}>
            {date || "—"} •{" "}
            {lastRefresh && <span>refreshed {formatTime(lastRefresh.toISOString())}</span>}
          </div>
        </div>
        <button
          onClick={fetchData}
          style={{
            background: T.surface,
            border: `1px solid ${T.border}`,
            borderRadius: 6,
            padding: "8px 16px",
            color: T.text,
            fontSize: 12,
            fontFamily: "'JetBrains Mono', monospace",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span style={{ display: "inline-block", animation: loading ? "spin 1s linear infinite" : "none" }}>⟳</span>
          Refresh
        </button>
      </div>

      <div style={{ padding: "24px 32px", maxWidth: 1100, margin: "0 auto" }}>
        {/* ── Combined Stats ── */}
        <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
          <StatCard label="Total Signals" value={cs.total} sub="NQ + QQQ combined" />
          <StatCard label="Fired Today" value={cs.fired} accent={T.green} />
          <StatCard label="Silent" value={cs.silent} accent={cs.silent > 0 ? T.red : T.green} />
          <StatCard
            label="Health"
            value={cs.total > 0 ? `${Math.round((cs.fired / cs.total) * 100)}%` : "—"}
            accent={cs.silent > 3 ? T.amber : T.green}
          />
        </div>

        {/* ── Toolbar ── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 16,
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 2,
              background: T.surface,
              border: `1px solid ${T.border}`,
              borderRadius: 6,
              padding: 2,
            }}
          >
            {[
              { key: "all", label: "All" },
              { key: "active", label: "Active" },
              { key: "silent", label: "Silent" },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                style={{
                  background: filter === key ? T.borderHi : "transparent",
                  border: "none",
                  borderRadius: 4,
                  padding: "6px 14px",
                  fontSize: 11,
                  fontWeight: 600,
                  color: filter === key ? T.text : T.textDim,
                  cursor: "pointer",
                  fontFamily: "'JetBrains Mono', monospace",
                }}
              >
                {label}
              </button>
            ))}
          </div>

          <div style={{ position: "relative" }}>
            <input
              type="text"
              placeholder="Search signal…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                background: T.surface,
                border: `1px solid ${T.border}`,
                borderRadius: 6,
                padding: "8px 12px 8px 32px",
                fontSize: 12,
                fontFamily: "'JetBrains Mono', monospace",
                color: T.text,
                width: 200,
                outline: "none",
              }}
              onFocus={(e) => (e.target.style.borderColor = T.blue)}
              onBlur={(e) => (e.target.style.borderColor = T.border)}
            />
            <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: T.textMuted }}>
              ⌕
            </span>
          </div>
        </div>

        {/* ── Asset Panels ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {HEALTH_ASSETS.map((asset) => (
            <AssetPanel
              key={asset}
              asset={asset}
              data={assetData[asset]}
              expanded={assetExpanded[asset]}
              onToggle={() => toggleAsset(asset)}
              tfExpanded={tfExpanded}
              onTfToggle={toggleTf}
              filter={filter}
              search={search}
            />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: ${T.bg}; }
        ::-webkit-scrollbar-thumb { background: ${T.borderHi}; border-radius: 3px; }
      `}</style>
    </div>
  );
}
